import React, { useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import { doc, getDoc } from "firebase/firestore"
import { db } from "../firebase"

const MarkList = () => {
	const [classes, setClasses] = useState([])
	const [loading, setLoading] = useState(true)
	const [loadError, setLoadError] = useState(false)

	const [selectedClassId, setSelectedClassId] = useState("")
	const [selectedCourseId, setSelectedCourseId] = useState("")
	const [rollNo, setRollNo] = useState("")
	const [result, setResult] = useState(null)

	useEffect(() => {
		getDoc(doc(db, "marklist", "data"))
			.then((snap) => {
				if (snap.exists() && Array.isArray(snap.data().classes)) {
					setClasses(snap.data().classes)
				}
				setLoading(false)
			})
			.catch((error) => {
				console.error("Could not load mark list:", error)
				setLoadError(true)
				setLoading(false)
			})
	}, [])

	const selectedClass = useMemo(
		() => classes.find((item) => item.id === selectedClassId) || null,
		[classes, selectedClassId]
	)
	const courses = useMemo(() => selectedClass?.courses || [], [selectedClass])
	const selectedCourse = useMemo(
		() => courses.find((item) => item.id === selectedCourseId) || null,
		[courses, selectedCourseId]
	)

	const handleClassChange = (e) => {
		setSelectedClassId(e.target.value)
		setSelectedCourseId("")
		setResult(null)
	}

	const handleCourseChange = (e) => {
		setSelectedCourseId(e.target.value)
		setResult(null)
	}

	const handleRollChange = (e) => {
		setRollNo(e.target.value)
		setResult(null)
	}

	const handleSubmit = (e) => {
		e.preventDefault()
		if (!selectedCourse) return

		const roll = rollNo.trim().toLowerCase()
		const student = roll
			? selectedCourse.students.find(
					(s) => (s.rollNo || "").trim().toLowerCase() === roll
			  )
			: null

		if (!student) {
			setResult({ status: "notfound" })
			return
		}

		const exams = (selectedCourse.exams || []).map((exam) => {
			const scores = exam.scores?.[student.id] || {}
			const rows = exam.subjects.map((sub) => ({
				id: sub.id,
				name: sub.name,
				total: Number(sub.total || 0),
				mark: Number(scores[sub.id] || 0),
			}))
			const obtained = rows.reduce((sum, r) => sum + r.mark, 0)
			const maximum = rows.reduce((sum, r) => sum + r.total, 0)
			const percentage = maximum > 0 ? (obtained / maximum) * 100 : 0
			return { id: exam.id, name: exam.name, rows, obtained, maximum, percentage }
		})

		setResult({
			status: "found",
			student,
			className: selectedClass.name,
			courseName: selectedCourse.name,
			exams,
		})
	}

	return (
		<div className="marklist-page">
			<motion.div
				className="marklist-card"
				initial={{ opacity: 0, y: 28 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.55 }}
			>
				<div className="marklist-card-head">
					<span className="section-eyebrow">Results Portal</span>
					<h1 className="marklist-title montserrat">Mark List Search</h1>
					<p className="marklist-subtitle">
						Select your class and course, then enter your roll number to view your marks.
					</p>
				</div>

				{loading ? (
					<p className="marklist-loading">
						<i className="ri-loader-4-line"></i> Loading results…
					</p>
				) : loadError ? (
					<div className="marklist-result is-error">
						<i className="ri-error-warning-line"></i>
						<div>
							<strong>Couldn't load results</strong>
							<p>Please check your connection and try again.</p>
						</div>
					</div>
				) : (
					<form onSubmit={handleSubmit} className="marklist-form">
						<div className="marklist-form-grid">
							<div className="admission-field-group">
								<label htmlFor="markClass">
									Select Class <span className="required">*</span>
								</label>
								<select
									id="markClass"
									name="markClass"
									className="admission-field"
									value={selectedClassId}
									onChange={handleClassChange}
									required
								>
									<option value="">Choose a class</option>
									{classes.map((item) => (
										<option key={item.id} value={item.id}>
											{item.name}
										</option>
									))}
								</select>
							</div>

							<div className="admission-field-group">
								<label htmlFor="markCourse">
									Select Course <span className="required">*</span>
								</label>
								<select
									id="markCourse"
									name="markCourse"
									className="admission-field"
									value={selectedCourseId}
									onChange={handleCourseChange}
									disabled={!selectedClassId}
									required
								>
									<option value="">
										{selectedClassId ? "Choose a course" : "Select a class first"}
									</option>
									{courses.map((item) => (
										<option key={item.id} value={item.id}>
											{item.name}
										</option>
									))}
								</select>
							</div>
						</div>

						<div className="admission-field-group">
							<label htmlFor="markRoll">
								Roll Number <span className="required">*</span>
							</label>
							<input
								id="markRoll"
								name="markRoll"
								type="text"
								className="admission-field"
								placeholder="Enter your roll number"
								value={rollNo}
								onChange={handleRollChange}
								disabled={!selectedCourseId}
								required
							/>
						</div>

						<button type="submit" className="submit-button marklist-submit">
							<i className="ri-search-line"></i> View Mark List
						</button>
					</form>
				)}

				{result && result.status === "notfound" && (
					<div className="marklist-result is-error">
						<i className="ri-user-search-line"></i>
						<div>
							<strong>No matching record</strong>
							<p>
								No student with roll number “{rollNo.trim()}” was found in{" "}
								{selectedCourse?.name}. Please check the roll number and try again.
							</p>
						</div>
					</div>
				)}

				{result && result.status === "found" && (
					<div className="mark-report">
						<div className="mark-report-head">
							<div>
								<h2>{result.student.name}</h2>
								<p>
									{result.className} · {result.courseName} · Roll{" "}
									{result.student.rollNo || "—"}
								</p>
							</div>
						</div>

						{result.exams.length === 0 ? (
							<p className="marklist-loading">No exams have been published for this course yet.</p>
						) : (
							result.exams.map((exam) => {
								const passed = exam.percentage >= 35
								return (
									<div className="mark-report-exam" key={exam.id}>
										<div className="mark-report-exam-head">
											<h3>{exam.name}</h3>
											<span className={`mark-badge is-${passed ? "pass" : "fail"}`}>
												{exam.percentage.toFixed(1)}% · {passed ? "Pass" : "Fail"}
											</span>
										</div>

										{exam.rows.length === 0 ? (
											<p className="mark-report-empty">No subjects in this exam.</p>
										) : (
											<table className="mark-report-table">
												<thead>
													<tr>
														<th>Subject</th>
														<th>Mark</th>
														<th>Out of</th>
														<th>Result</th>
													</tr>
												</thead>
												<tbody>
													{exam.rows.map((row) => {
														const rowPass =
															row.total > 0 && row.mark / row.total >= 0.35
														return (
															<tr key={row.id}>
																<td>{row.name}</td>
																<td>{row.mark}</td>
																<td>{row.total}</td>
																<td>
																	<span
																		className={`mark-pill is-${rowPass ? "pass" : "fail"}`}
																	>
																		{rowPass ? "Pass" : "Fail"}
																	</span>
																</td>
															</tr>
														)
													})}
												</tbody>
												<tfoot>
													<tr>
														<td>Total</td>
														<td>{exam.obtained}</td>
														<td>{exam.maximum}</td>
														<td></td>
													</tr>
												</tfoot>
											</table>
										)}
									</div>
								)
							})
						)}
					</div>
				)}
			</motion.div>
		</div>
	)
}

export default MarkList
